import React, { useMemo, useState } from "react";
import { formatCurrency } from "utils/helperFunctions";

const MonthlyPriceRow = ({ month, entry, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const label = new Date(`${month}-01T12:00:00`).toLocaleDateString("en-AU", {
    month: "long",
    year: "numeric",
  });

  const handleSave = () => {
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && parsed >= 0) {
      onSave({ month, amount: parsed });
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-darkgray w-32 shrink-0">{label}</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") setEditing(false);
          }}
          autoFocus
          className="border border-mediumgray rounded px-2 py-0.5 text-sm w-28 focus:outline-none focus:border-black"
          placeholder="0.00"
        />
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="gray-button"
        >
          Cancel
        </button>
        <button type="button" onClick={handleSave} className="black-button">
          Save
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-darkgray w-32 shrink-0">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-darkergray">
        {formatCurrency(entry?.amount ?? 0)}
      </span>
      <button
        type="button"
        onClick={() => {
          setValue(String(entry?.amount ?? ""));
          setEditing(true);
        }}
        className="gray-button"
      >
        Edit
      </button>
    </div>
  );
};

/**
 * Shared monthly pricing header slot for drill-down modals.
 *
 * Props:
 *   provider       — platform key (e.g. "openai", "twilio")
 *   monthlyPrices  — { "YYYY-MM": { _id, amount }, ... }
 *   dateValue      — { startDate, endDate } in YYYY-MM-DD format
 *   onSetMonthlyPrice — ({ provider, month, amount }) => void
 */
const PlatformMonthlyPricing = ({
  provider,
  monthlyPrices = {},
  dateValue = {},
  onSetMonthlyPrice,
}) => {
  const isDateBounded = !!(dateValue.startDate && dateValue.endDate);

  // A monthly cost is stored against the 1st of its month, and the summary
  // only returns costs whose 1st falls inside the range. So only offer months
  // whose 1st is in range: a month the range starts part-way through would
  // show $0.00 here even when a cost is saved for it.
  const rangeMonths = useMemo(() => {
    const from = dateValue?.startDate;
    const to = dateValue?.endDate;
    if (!from || !to) return [];
    const months = [];
    const [fy, fm] = from.split("-").map(Number);
    const [ty, tm] = to.split("-").map(Number);
    let year = fy,
      month = fm;
    while (year < ty || (year === ty && month <= tm)) {
      const key = `${year}-${String(month).padStart(2, "0")}`;
      if (`${key}-01` >= from) months.push(key);
      month++;
      if (month > 12) {
        month = 1;
        year++;
      }
    }
    return months;
  }, [dateValue?.startDate, dateValue?.endDate]);

  if (!isDateBounded) {
    return (
      <p className="text-xs text-darkgray italic">
        Select a date range to set the monthly cost.
      </p>
    );
  }

  if (!rangeMonths.length) {
    return (
      <p className="text-xs text-darkgray italic">
        Select a date range that includes the 1st of a month to set its
        monthly cost.
      </p>
    );
  }

  return (
    <div className="px-4 py-3 bg-lightergray border border-lightgray rounded-lg">
      <div className="text-xs uppercase tracking-wide text-darkgray mb-2">
        Monthly cost
      </div>
      <div className="flex flex-col gap-1.5">
        {rangeMonths.map((m) => (
          <MonthlyPriceRow
            key={m}
            month={m}
            entry={monthlyPrices[m] ?? null}
            onSave={({ month, amount }) =>
              onSetMonthlyPrice({ provider, month, amount })
            }
          />
        ))}
      </div>
    </div>
  );
};

export default PlatformMonthlyPricing;
export { MonthlyPriceRow };
